import { SignatureEmployeeData, TemplateId, SocialLinks, SocialItem } from '../types';

/**
 * Wraps signature HTML in a complete standalone HTML document with explicit UTF-8 charset
 * and styling suitable for saving or opening directly in any browser (Safari, Chrome on macOS/Windows).
 */
export function wrapWithFullHtmlDocument(signatureHtml: string, title: string = 'Podpis e-mail - CerebroSign'): string {
  return `<!DOCTYPE html>
<html lang="pl">
<head>
  <meta charset="UTF-8">
  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${escapeHtml(title)}</title>
</head>
<body style="margin: 0; padding: 24px; background-color: #ffffff; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; -webkit-font-smoothing: antialiased;">
${signatureHtml}
</body>
</html>`;
}

/**
 * Downloads signature HTML as a .html file with explicit UTF-8 encoding and UTF-8 Byte Order Mark (\uFEFF)
 * so that opening it in Safari or Chrome on macOS always displays Polish characters (ą, ć, ę, ł, ń, ó, ś, ź, ż) flawlessly.
 */
export function downloadSignatureHtmlFile(signatureHtml: string, filename: string = 'stopka-cerebrosign.html'): void {
  const fullHtml = wrapWithFullHtmlDocument(signatureHtml);
  // Prepend UTF-8 BOM (\uFEFF) so macOS QuickLook, Safari, Chrome and TextEdit immediately detect UTF-8
  const blob = new Blob(['\uFEFF', fullHtml], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.html') ? filename : `${filename}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Copies signature HTML to clipboard with UTF-8 meta information and rich HTML payload
 */
export async function copySignatureToClipboard(signatureHtml: string): Promise<void> {
  const metaHtml = signatureHtml.includes('charset=UTF-8')
    ? signatureHtml
    : `<meta http-equiv="Content-Type" content="text/html; charset=UTF-8">\n${signatureHtml}`;

  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    try {
      const textBlob = new Blob([metaHtml], { type: 'text/plain;charset=utf-8' });
      const htmlBlob = new Blob([metaHtml], { type: 'text/html;charset=utf-8' });
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': textBlob,
          'text/html': htmlBlob,
        }),
      ]);
      return;
    } catch {
      // Fallback to writeText below if ClipboardItem write fails
    }
  }

  await navigator.clipboard.writeText(metaHtml);
}

function escapeHtml(text?: string): string {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function generateFallbackAvatar(name: string, primaryColor: string = '#0284c7'): string {
  const cleanColor = (primaryColor || '#0284c7').replace('#', '') || '0284c7';
  const cleanName = encodeURIComponent(name || 'CS');
  return `https://ui-avatars.com/api/?name=${cleanName}&background=${cleanColor}&color=ffffff&size=128&bold=true&rounded=true&format=png`;
}

export const DEFAULT_CEREBRO_LOGO = 'https://cerebro-sign.web.app/img/signatures/cerebro-logo.png';

/**
 * Calculates rigid width and height for image bounding box,
 * ensuring natural aspect ratio without stretching or squishing in Outlook / Gmail.
 */
export function calculateFittedDimensions(
  origW?: number,
  origH?: number,
  maxW: number = 70,
  maxH: number = 60
): { width: number; height: number } {
  if (!origW || !origH || origW <= 0 || origH <= 0) {
    return { width: Math.min(maxW, 60), height: Math.min(maxH, 60) };
  }
  const ratio = Math.min(maxW / origW, maxH / origH);
  return {
    width: Math.max(1, Math.round(origW * ratio)),
    height: Math.max(1, Math.round(origH * ratio)),
  };
}

const SOCIAL_ICONS: Record<string, (color: string) => string> = {
  linkedin: () => 'https://cerebro-sign.web.app/img/signatures/linkedin.png',
  facebook: () => 'https://cerebro-sign.web.app/img/signatures/facebook.png',
  instagram: () => 'https://cerebro-sign.web.app/img/signatures/instagram.png',
  twitter: () => 'https://cerebro-sign.web.app/img/signatures/twitter.png',
  github: () => 'https://cerebro-sign.web.app/img/signatures/github.png',
  youtube: () => 'https://cerebro-sign.web.app/img/signatures/youtube.png',
};

/**
 * Renderuje ikony social media.
 * ZGODNIE Z WYMAGANIEM:
 * Jeśli pole `url` jest puste LUB `enabled === false`, ikona NIE MOŻE pojawić się w wygenerowanej tabeli HTML
 * (całkowity brak znacznika <td>/<a> dla tej platformy).
 */
function renderSocialIconsHtml(
  socials?: SocialItem[] | SocialLinks | Record<string, any>,
  primaryColor: string = '#0284c7'
): string {
  if (!socials) return '';

  let items: SocialItem[] = [];

  if (Array.isArray(socials)) {
    items = socials;
  } else if (typeof socials === 'object') {
    items = Object.entries(socials).map(([key, val]) => {
      if (val && typeof val === 'object' && 'platform' in val) {
        return val as SocialItem;
      }
      return {
        platform: key,
        url: typeof val === 'string' ? val : '',
        enabled: Boolean(val),
      };
    });
  }

  // Odfiltruj wyłączone lub z pustym adresem URL
  const activeItems = items.filter(
    (item) => item && item.enabled === true && Boolean(item.url && item.url.trim()) && Boolean(SOCIAL_ICONS[item.platform.toLowerCase()])
  );

  if (activeItems.length === 0) return '';

  const iconsHtml = activeItems
    .map((item) => {
      const platformKey = item.platform.toLowerCase();
      const iconUrl = SOCIAL_ICONS[platformKey](primaryColor);
      const safeUrl = escapeHtml(item.url.trim());
      const safePlatform = escapeHtml(item.platform);
      return `<a href="${safeUrl}" target="_blank" style="display: inline-block; margin-right: 6px; text-decoration: none;">
        <img src="${iconUrl}" alt="${safePlatform}" width="18" height="18" style="display: block; outline: none; border: none; text-decoration: none; width: 18px; height: 18px; font-size: 11px; color: #94a3b8; font-family: sans-serif;" />
      </a>`;
    })
    .join('');

  return `<div style="padding-top: 6px; line-height: 1;">${iconsHtml}</div>`;
}

export const VIRAL_BRANDING_ROW = `<tr>
  <td colspan="4" style="padding-top: 10px; font-size: 10px; color: #94a3b8; font-family: Trebuchet MS, Arial, sans-serif; text-align: left; line-height: 14px; -webkit-text-size-adjust: none; mso-line-height-rule: exactly;">
    Stworzone przez <a href="https://sign.cerebrolabs.pl/?utm_source=user_signature&utm_medium=email&utm_campaign=viral_free" target="_blank" rel="noopener noreferrer" style="color: #0284c7; text-decoration: none; font-weight: 600; display: inline;">CerebroSign</a>
  </td>
</tr>`;

function renderFreemiumWatermark(isFreemium: boolean): string {
  if (!isFreemium) return '';
  return VIRAL_BRANDING_ROW;
}

export function enforcePlanBranding(html: string, plan: string | undefined): string {
  const isPaid = plan === 'team5' || plan === 'team10' || plan === 'business25' || plan === 'enterprise';

  if (isPaid) {
    return html.replace(/<tr>\s*<td[^>]*>[\s\S]*?sign\.cerebrolabs\.pl\/\?utm_source=user_signature[\s\S]*?<\/td>\s*<\/tr>/gi, '');
  }

  if (!html.includes('utm_campaign=viral_free') && !html.includes('sign.cerebrolabs.pl/?utm_source=user_signature')) {
    const lastTableIdx = html.lastIndexOf('</table>');
    if (lastTableIdx !== -1) {
      return html.slice(0, lastTableIdx) + VIRAL_BRANDING_ROW + html.slice(lastTableIdx);
    }
    return html + `<table style="width: 100%; border-collapse: collapse;">${VIRAL_BRANDING_ROW}</table>`;
  }

  return html;
}

/**
 * Client signature HTML generator supporting all 4 templates.
 */
export function generateClientSignatureHtml(data: SignatureEmployeeData): string {
  const templateId: TemplateId = data.templateId || 'modern_horizontal';
  const primaryColor = data.primaryColor || '#0284c7';
  const toggles = data.toggles || {
    showAvatar: true,
    avatarShape: 'circle',
    showJobTitle: true,
    showDepartment: true,
    showCompany: true,
    showPhone: true,
    showEmail: true,
    showWebsite: true,
    showTaxId: true,
    showAddress: true,
    showSocials: true,
    showBanner: true,
    showDisclaimer: true,
    showFreemiumBranding: true,
  };

  const fullName = escapeHtml(data.fullName || 'Jan Kowalski');
  const jobTitle = escapeHtml(data.jobTitle || 'Solutions Architect');
  const department = escapeHtml(data.department || 'Cloud Engineering');
  const companyName = escapeHtml(data.companyName || 'CerebroLabs Sp. z o.o.');

  // Precedence / Fallback logic (General Template vs Personal Employee Data)
  // Phone: (useCustomPhone && phone) ? phone : (companyPhone || phone || default)
  const rawPhone = (data.useCustomPhone && data.phone)
    ? data.phone
    : (data.companyPhone || data.phone || '+48 22 123 45 67');
  const phone = escapeHtml(rawPhone);

  // Mobile: displayed if showMobile is true and mobile is set
  const mobile = (data.showMobile && data.mobile) ? escapeHtml(data.mobile) : '';

  // Email: employee account email or custom alias
  const email = escapeHtml(data.emailAlias || data.email || 'jan.kowalski@cerebrolabs.io');

  // Website: custom employee landing page if enabled, else template website
  const websiteUrl = (data.useCustomWebsite && data.customWebsiteUrl)
    ? data.customWebsiteUrl
    : (data.websiteUrl || 'https://cerebrolabs.io');

  // Address: custom employee branch address if enabled, else template address
  const address = escapeHtml(
    (data.useCustomAddress && data.customAddress)
      ? data.customAddress
      : (data.address || 'ul. Prosta 20, 00-850 Warszawa')
  );

  const nip = escapeHtml(data.nip || '5252824941');
  const regon = escapeHtml(data.regon || '386928102');
  const logoUrl =
    data.logoUrl && !data.logoUrl.startsWith('data:image/svg')
      ? data.logoUrl
      : DEFAULT_CEREBRO_LOGO;

  // Avatar: employee personal avatar takes precedence; fallback to default placeholder, then initials
  const rawAvatar = data.avatarUrl && !data.avatarUrl.startsWith('data:image/svg') ? data.avatarUrl : null;
  const avatarUrl = rawAvatar || data.defaultAvatarPlaceholder || generateFallbackAvatar(fullName, primaryColor);

  const borderRadius =
    toggles.avatarShape === 'square' ? '0px' : toggles.avatarShape === 'rounded' ? '8px' : '50%';

  const isPaidPlan = data.plan === 'team5' || data.plan === 'team10' || data.plan === 'business25' || data.plan === 'enterprise';
  const isFreemium = !isPaidPlan;

  const utmParam = data.bannerLink?.includes('utm_source')
    ? ''
    : '?utm_source=email_signature&utm_medium=cerebro_sign&utm_campaign=brand_awareness';
  const bannerLink = data.bannerLink
    ? `${data.bannerLink}${utmParam}`
    : `${websiteUrl}${utmParam}`;
  const bannerUrl = data.bannerUrl;
  const bannerAlt = escapeHtml(data.bannerAlt || 'Baner promocyjny');

  const disclaimerText = escapeHtml(
    data.disclaimer ||
      'Treść tej wiadomości zawiera informacje poufne przeznaczone wyłącznie dla adresata. Jeśli otrzymałeś tę wiadomość omyłkowo, prosimy o jej usunięcie i poinformowanie nadawcy. Zgodność z RODO / GDPR.'
  );

  const socialsHtml = toggles.showSocials !== false ? renderSocialIconsHtml(data.socials, primaryColor) : '';

  let signatureHtml = '';

  // 1. Template 1: Modern Horizontal
  if (templateId === 'modern_horizontal') {
    const modernLogoDims = calculateFittedDimensions(data.logoWidth, data.logoHeight, 70, 60);
    signatureHtml = `<!-- CerebroSign Modern Horizontal Start -->
<table cellpadding="0" cellspacing="0" border="0" width="520" style="width: 520px; max-width: 520px; font-family: 'Trebuchet MS', 'Lucida Sans Unicode', sans-serif; font-size: 13px; line-height: 1.4; color: #1e293b; background-color: #ffffff; border-collapse: collapse;">
  <tr>
    <td style="padding: 14px 16px; border: 1px solid #e2e8f0; border-radius: 8px;">
      <table cellpadding="0" cellspacing="0" border="0" width="488" style="width: 488px; max-width: 488px; border-collapse: collapse; table-layout: fixed;">
        <tr>
          ${
            toggles.showAvatar !== false
              ? `<td valign="middle" align="center" width="68" style="width: 68px; vertical-align: middle; padding-right: 14px;">
                  <img src="${avatarUrl}" alt="${fullName}" width="64" height="64" style="display: block; outline: none; border: none; text-decoration: none; width: 64px; height: 64px; border-radius: ${borderRadius}; object-fit: cover; border: 2px solid ${primaryColor}; font-size: 11px; color: #94a3b8; font-family: sans-serif;" />
                </td>
                <td valign="middle" width="1" style="width: 1px; min-width: 1px; background-color: ${primaryColor}; vertical-align: middle; padding: 0;">
                  <div style="width: 1px; height: 72px; background-color: ${primaryColor}; line-height: 1px; font-size: 1px;">&nbsp;</div>
                </td>`
              : ''
          }
          <td valign="middle" style="vertical-align: middle; padding-left: 14px; padding-right: 12px; word-break: break-word; overflow: hidden;">
            <div style="font-size: 15px; font-weight: bold; color: #0f172a; line-height: 1.2;">${fullName}</div>
            ${
              toggles.showJobTitle !== false
                ? `<div style="font-size: 11px; font-weight: 700; color: ${primaryColor}; text-transform: uppercase; letter-spacing: 0.5px; padding: 2px 0 4px;">
                    ${jobTitle}${toggles.showDepartment !== false && department ? ` &bull; ${department}` : ''}
                  </div>`
                : ''
            }
            <div style="font-size: 11px; color: #475569; line-height: 1.4;">
              ${toggles.showPhone !== false && phone ? `<span style="color: #64748b;">tel:</span> <a href="tel:${phone.replace(/\s+/g, '')}" style="color: #0f172a; text-decoration: none; font-weight: 500;">${phone}</a> &nbsp;|&nbsp; ` : ''}
              ${mobile ? `<span style="color: #64748b;">kom:</span> <a href="tel:${mobile.replace(/\s+/g, '')}" style="color: #0f172a; text-decoration: none; font-weight: 500;">${mobile}</a> &nbsp;|&nbsp; ` : ''}
              ${toggles.showEmail !== false ? `<span style="color: #64748b;">e:</span> <a href="mailto:${email}" style="color: ${primaryColor}; text-decoration: none; font-weight: 500;">${email}</a>` : ''}
            </div>
            ${
              toggles.showWebsite !== false || toggles.showTaxId !== false
                ? `<div style="font-size: 10px; color: #94a3b8; padding-top: 3px;">
                    ${toggles.showWebsite !== false ? `<a href="${websiteUrl}" target="_blank" style="color: #64748b; text-decoration: underline;">${websiteUrl.replace(/^https?:\/\//, '')}</a>` : ''}
                    ${toggles.showTaxId !== false && nip ? ` &bull; NIP: ${nip}` : ''}
                    ${toggles.showTaxId !== false && regon ? ` &bull; REGON: ${regon}` : ''}
                  </div>`
                : ''
            }
            ${socialsHtml}
          </td>
          ${
            toggles.showCompany !== false
              ? `<td valign="middle" align="right" width="80" style="width: 80px; min-width: 80px; max-width: 80px; vertical-align: middle; text-align: right; padding-left: 10px;">
                  <a href="${websiteUrl}" target="_blank" style="text-decoration: none;">
                    <img src="${logoUrl}" alt="${companyName}" width="${modernLogoDims.width}" height="${modernLogoDims.height}" border="0" style="display: block; width: ${modernLogoDims.width}px; min-width: ${modernLogoDims.width}px; max-width: ${modernLogoDims.width}px; height: ${modernLogoDims.height}px; min-height: ${modernLogoDims.height}px; max-height: ${modernLogoDims.height}px; border-radius: 8px; outline: none; border: 0; font-size: 11px; color: #94a3b8; font-family: sans-serif;" />
                  </a>
                </td>`
              : ''
          }
        </tr>

        ${
          toggles.showBanner !== false
            ? `<tr>
                <td colspan="4" style="padding-top: 12px;">
                  ${
                    bannerUrl
                      ? `<a href="${bannerLink}" target="_blank" style="display: block; text-decoration: none;">
                          <img src="${bannerUrl}" alt="${bannerAlt}" width="488" height="85" style="display: block; outline: none; border: none; text-decoration: none; width: 488px; max-width: 488px; height: 85px; border-radius: 6px; border: 1px solid #e2e8f0; font-size: 11px; color: #94a3b8; font-family: sans-serif;" />
                        </a>`
                      : `<table cellpadding="0" cellspacing="0" border="0" width="100%" style="width: 100%; background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px;">
                          <tr>
                            <td style="padding: 8px 12px; font-size: 11px; color: #334155;">
                              <span style="font-weight: bold; color: ${primaryColor};">⚡ ${companyName}:</span> Innowacyjne rozwiązania chmurowe i zarządzanie stopkami.
                            </td>
                            <td align="right" style="padding: 8px 12px; white-space: nowrap;">
                              <a href="${bannerLink}" target="_blank" style="display: inline-block; background-color: ${primaryColor}; color: #ffffff; text-decoration: none; font-weight: bold; font-size: 10px; padding: 4px 10px; border-radius: 4px; text-transform: uppercase;">
                                Sprawdź →
                              </a>
                            </td>
                          </tr>
                        </table>`
                  }
                </td>
              </tr>`
            : ''
        }

        ${
          toggles.showDisclaimer !== false && disclaimerText
            ? `<tr>
                <td colspan="4" style="padding-top: 8px; font-size: 9px; color: #94a3b8; line-height: 1.3; border-top: 1px solid #f1f5f9;">
                  ${disclaimerText}
                </td>
              </tr>`
            : ''
        }

        ${renderFreemiumWatermark(isFreemium)}
      </table>
    </td>
  </tr>
</table>
<!-- CerebroSign Modern Horizontal End -->`;
  // 2. Template 2: Clean Minimal
  } else if (templateId === 'clean_minimal') {
    const cleanMinimalLogoDims = calculateFittedDimensions(data.logoWidth, data.logoHeight, 95, 50);
    signatureHtml = `<!-- CerebroSign Clean Minimal Start -->
<table cellpadding="0" cellspacing="0" border="0" width="500" style="width: 500px; max-width: 500px; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; font-size: 12px; line-height: 1.4; color: #1e293b; border-collapse: collapse;">
  <tr>
    <td style="padding-bottom: 6px; border-bottom: 2px solid ${primaryColor};">
      <table cellpadding="0" cellspacing="0" border="0" width="100%">
        <tr>
          <td valign="top">
            <div style="font-size: 16px; font-weight: 700; color: #0f172a; letter-spacing: -0.2px;">${fullName}</div>
            <div style="font-size: 11px; color: ${primaryColor}; font-weight: 600; text-transform: uppercase; margin-top: 2px;">
              ${jobTitle} &bull; ${companyName}
            </div>
          </td>
          <td valign="top" align="right" width="100">
            ${
              toggles.showCompany !== false && logoUrl
                ? `<a href="${websiteUrl}" target="_blank" style="text-decoration: none;">
                    <img src="${logoUrl}" alt="${companyName}" width="${cleanMinimalLogoDims.width}" height="${cleanMinimalLogoDims.height}" style="display: block; outline: none; border: none; text-decoration: none; width: ${cleanMinimalLogoDims.width}px; max-width: ${cleanMinimalLogoDims.width}px; height: ${cleanMinimalLogoDims.height}px; font-size: 11px; color: #94a3b8; font-family: sans-serif;" />
                  </a>`
                : ''
            }
          </td>
        </tr>
      </table>
    </td>
  </tr>
  <tr>
    <td style="padding-top: 8px;">
      <table cellpadding="0" cellspacing="0" border="0" width="100%">
        <tr>
          <td style="font-size: 11px; color: #64748b;">
            ${toggles.showPhone !== false && phone ? `<span>T:</span> <a href="tel:${phone.replace(/\s+/g, '')}" style="color: #0f172a; text-decoration: none; font-weight: 500;">${phone}</a> &nbsp;|&nbsp; ` : ''}
            ${mobile ? `<span>K:</span> <a href="tel:${mobile.replace(/\s+/g, '')}" style="color: #0f172a; text-decoration: none; font-weight: 500;">${mobile}</a> &nbsp;|&nbsp; ` : ''}
            <span>E:</span> <a href="mailto:${email}" style="color: ${primaryColor}; text-decoration: none;">${email}</a>
            &nbsp;|&nbsp;
            <span>W:</span> <a href="${websiteUrl}" target="_blank" style="color: #0f172a; text-decoration: none;">${websiteUrl.replace(/^https?:\/\//, '')}</a>
          </td>
        </tr>
        ${
          toggles.showTaxId !== false && (nip || address)
            ? `<tr>
                <td style="font-size: 10px; color: #94a3b8; padding-top: 4px;">
                  ${address ? `${address} &bull; ` : ''}${nip ? `NIP: ${nip}` : ''}
                </td>
              </tr>`
            : ''
        }
        ${
          socialsHtml
            ? `<tr><td style="padding-top: 6px;">${socialsHtml}</td></tr>`
            : ''
        }
        ${
          toggles.showDisclaimer !== false && disclaimerText
            ? `<tr>
                <td style="padding-top: 8px; font-size: 9px; color: #94a3b8; line-height: 1.3;">
                  ${disclaimerText}
                </td>
              </tr>`
            : ''
        }
        ${renderFreemiumWatermark(isFreemium)}
      </table>
    </td>
  </tr>
</table>
<!-- CerebroSign Clean Minimal End -->`;
  // 3. Template 3: Corporate Compact (Executive)
  } else if (templateId === 'corporate_compact') {
    const compactLogoDims = calculateFittedDimensions(data.logoWidth, data.logoHeight, 90, 45);
    signatureHtml = `<!-- CerebroSign Corporate Compact Start -->
<table cellpadding="0" cellspacing="0" border="0" width="500" style="width: 500px; max-width: 500px; font-family: Arial, sans-serif; font-size: 11px; line-height: 1.35; color: #334155; border-collapse: collapse; border-left: 3px solid ${primaryColor};">
  <tr>
    <td style="padding: 2px 0 2px 10px;">
      <table cellpadding="0" cellspacing="0" border="0" width="100%" style="width: 100%;">
        <tr>
          ${
            toggles.showAvatar !== false && avatarUrl
              ? `<td valign="middle" width="46" style="width: 46px; padding-right: 10px; vertical-align: middle;">
                  <img src="${avatarUrl}" alt="${fullName}" width="42" height="42" style="display: block; outline: none; border: none; text-decoration: none; width: 42px; height: 42px; border-radius: ${borderRadius}; object-fit: cover; border: 1.5px solid ${primaryColor}; font-size: 11px; color: #94a3b8; font-family: sans-serif;" />
                </td>`
              : ''
          }
          <td valign="middle" style="vertical-align: middle;">
            <div style="font-size: 13px; font-weight: bold; color: #0f172a; line-height: 1.25;">
              ${fullName} ${jobTitle ? `<span style="font-size: 11px; font-weight: normal; color: ${primaryColor};">| ${jobTitle}${toggles.showDepartment !== false && department ? ` &bull; ${department}` : ''}</span>` : ''}
            </div>
            <div style="font-size: 10px; color: #64748b; margin-top: 2px; line-height: 1.4;">
              <strong style="color: #0f172a;">${companyName}</strong> &bull;
              ${toggles.showPhone !== false && phone ? `<a href="tel:${phone.replace(/\s+/g, '')}" style="color: #334155; text-decoration: none;">${phone}</a> &bull; ` : ''}
              ${mobile ? `<a href="tel:${mobile.replace(/\s+/g, '')}" style="color: #334155; text-decoration: none;">kom. ${mobile}</a> &bull; ` : ''}
              <a href="mailto:${email}" style="color: ${primaryColor}; text-decoration: none;">${email}</a> &bull;
              <a href="${websiteUrl}" target="_blank" style="color: #64748b; text-decoration: none;">${websiteUrl.replace(/^https?:\/\//, '')}</a>
              ${toggles.showTaxId !== false && nip ? ` &bull; NIP: ${nip}` : ''}
              ${toggles.showAddress !== false && address ? ` &bull; ${address}` : ''}
            </div>
            ${socialsHtml ? `<div style="padding-top: 4px;">${socialsHtml}</div>` : ''}
            ${renderFreemiumWatermark(isFreemium)}
          </td>
          ${
            toggles.showCompany !== false && logoUrl
              ? `<td valign="middle" align="right" width="95" style="width: 95px; text-align: right; padding-left: 10px; vertical-align: middle;">
                  <a href="${websiteUrl}" target="_blank" style="text-decoration: none;">
                    <img src="${logoUrl}" alt="${companyName}" width="${compactLogoDims.width}" height="${compactLogoDims.height}" style="display: block; outline: none; border: none; text-decoration: none; width: ${compactLogoDims.width}px; max-width: ${compactLogoDims.width}px; height: ${compactLogoDims.height}px; font-size: 11px; color: #94a3b8; font-family: sans-serif;" />
                  </a>
                </td>`
              : ''
          }
        </tr>
      </table>
    </td>
  </tr>
  ${
    bannerUrl && toggles.showBanner !== false
      ? `<tr>
          <td style="padding-left: 10px; padding-top: 8px;">
            <a href="${bannerLink}" target="_blank" style="display: block; text-decoration: none;">
              <img src="${bannerUrl}" alt="${bannerAlt}" width="490" height="85" style="display: block; outline: none; border: none; text-decoration: none; width: 490px; max-width: 490px; height: 85px; border-radius: 4px; border: 1px solid #e2e8f0; font-size: 11px; color: #94a3b8; font-family: sans-serif;" />
            </a>
          </td>
        </tr>`
      : ''
  }
  ${
    toggles.showDisclaimer !== false && disclaimerText
      ? `<tr>
          <td style="padding-left: 10px; padding-top: 6px; font-size: 8.5px; color: #94a3b8; line-height: 1.25; border-top: 1px solid #f1f5f9;">
            ${disclaimerText}
          </td>
        </tr>`
      : ''
  }
</table>
<!-- CerebroSign Corporate Compact End -->`;
  // 4. Template 4: Marketing Focus
  } else {
    const marketingLogoDims = calculateFittedDimensions(data.logoWidth, data.logoHeight, 100, 55);
    signatureHtml = `<!-- CerebroSign Marketing Focus Start -->
<table cellpadding="0" cellspacing="0" border="0" width="520" style="width: 520px; max-width: 520px; font-family: 'Trebuchet MS', 'Lucida Sans', sans-serif; font-size: 13px; line-height: 1.4; color: #1e293b; background-color: #ffffff; border-collapse: collapse; border: 1px solid #e2e8f0; border-radius: 10px; overflow: hidden;">
  <tr>
    <td style="padding: 16px;">
      <table cellpadding="0" cellspacing="0" border="0" width="100%">
        <tr>
          ${
            toggles.showAvatar !== false
              ? `<td valign="middle" width="60" style="width: 60px; padding-right: 14px;">
                  <img src="${avatarUrl}" alt="${fullName}" width="56" height="56" style="display: block; outline: none; border: none; text-decoration: none; width: 56px; height: 56px; border-radius: ${borderRadius}; object-fit: cover; border: 2px solid ${primaryColor}; font-size: 11px; color: #94a3b8; font-family: sans-serif;" />
                </td>`
              : ''
          }
          <td valign="middle">
            <div style="font-size: 16px; font-weight: 800; color: #0f172a; line-height: 1.2;">${fullName}</div>
            <div style="font-size: 11px; font-weight: 700; color: ${primaryColor}; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 2px;">
              ${jobTitle} &bull; ${companyName}
            </div>
            <div style="font-size: 11px; color: #64748b; margin-top: 3px;">
              ${toggles.showPhone !== false && phone ? `<span>T:</span> <a href="tel:${phone.replace(/\s+/g, '')}" style="color: #0f172a; text-decoration: none; font-weight: 600;">${phone}</a> &nbsp;|&nbsp; ` : ''}
              ${mobile ? `<span>K:</span> <a href="tel:${mobile.replace(/\s+/g, '')}" style="color: #0f172a; text-decoration: none; font-weight: 600;">${mobile}</a> &nbsp;|&nbsp; ` : ''}
              <span>E:</span> <a href="mailto:${email}" style="color: ${primaryColor}; text-decoration: none; font-weight: 600;">${email}</a>
            </div>
          </td>
          ${
            toggles.showCompany !== false && logoUrl
              ? `<td valign="middle" align="right" width="110" style="width: 110px; vertical-align: middle; text-align: right;">
                  <a href="${websiteUrl}" target="_blank" style="text-decoration: none;">
                    <img src="${logoUrl}" alt="${companyName}" width="${marketingLogoDims.width}" height="${marketingLogoDims.height}" style="display: block; outline: none; border: none; text-decoration: none; width: ${marketingLogoDims.width}px; max-width: ${marketingLogoDims.width}px; height: ${marketingLogoDims.height}px; font-size: 11px; color: #94a3b8; font-family: sans-serif;" />
                  </a>
                </td>`
              : ''
          }
        </tr>

        <!-- Marketing CTA Card / Banner -->
        <tr>
          <td colspan="4" style="padding-top: 14px;">
            ${
              bannerUrl && toggles.showBanner !== false
                ? `<a href="${bannerLink}" target="_blank" style="display: block; text-decoration: none; margin-bottom: 10px;">
                    <img src="${bannerUrl}" alt="${bannerAlt}" width="488" height="85" style="display: block; outline: none; border: none; text-decoration: none; width: 488px; max-width: 488px; height: 85px; border-radius: 6px; border: 1px solid #e2e8f0; font-size: 11px; color: #94a3b8; font-family: sans-serif;" />
                  </a>`
                : ''
            }
            <table cellpadding="0" cellspacing="0" border="0" width="100%" style="width: 100%; background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%); border-radius: 8px; border: 1px solid #334155;">
              <tr>
                <td style="padding: 12px 14px; color: #ffffff;">
                  <div style="font-size: 12px; font-weight: 700; color: #38bdf8; text-transform: uppercase; letter-spacing: 0.5px;">
                    ${data.promoTitle || '⚡ Nowa Oferta Specjalna'}
                  </div>
                  <div style="font-size: 11px; color: #cbd5e1; margin-top: 2px;">
                    ${data.promoText || 'Skonsultuj projekt chmurowy i wdrożenie AI z ekspertami CerebroLabs.'}
                  </div>
                </td>
                <td align="right" style="padding: 12px 14px; white-space: nowrap;">
                  <a href="${bannerLink}" target="_blank" style="display: inline-block; background-color: ${primaryColor}; color: #ffffff; text-decoration: none; font-weight: 700; font-size: 11px; padding: 7px 14px; border-radius: 6px; box-shadow: 0 2px 8px rgba(0,0,0,0.2);">
                    ${data.promoCtaText || 'Umów Rozmowę →'}
                  </a>
                </td>
              </tr>
            </table>
          </td>
        </tr>

        ${
          socialsHtml
            ? `<tr><td colspan="4" style="padding-top: 10px;">${socialsHtml}</td></tr>`
            : ''
        }

        ${
          toggles.showDisclaimer !== false && disclaimerText
            ? `<tr>
                <td colspan="4" style="padding-top: 10px; font-size: 9px; color: #94a3b8; line-height: 1.3; border-top: 1px solid #f1f5f9;">
                  ${disclaimerText}
                </td>
              </tr>`
            : ''
        }

        ${renderFreemiumWatermark(isFreemium)}
      </table>
    </td>
  </tr>
</table>
<!-- CerebroSign Marketing Focus End -->`;
  }

  return `<meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />\n` + signatureHtml;
}

